import React, { Suspense } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import FallbackSpinner from "./@core/components/spinner";
import AuthGuard from "src/@core/components/auth/AuthGuard";
import UserLayout from "./layouts/UserLayout";
import BlankLayout from "./@core/layouts/BlankLayout";
import AclGuard from "./@core/components/auth/AclGuard";
import GuestGuard from "./@core/components/auth/GuestGuard";
import { defaultACLObj } from "./configs/acl";
import AuthLayout from "./layouts/AuthLayout";

const DashBoardPage = React.lazy(() => import("./pages/dashboard"));
const LoginPage = React.lazy(() => import("./pages/login"));
const ForgotPassword = React.lazy(() => import("./pages/login/forgotpassword"));
const ContactUsPage = React.lazy(() => import("./pages/contact-us"));
const TalkToExpertPage = React.lazy(() => import("./pages/talk-to-expert"));
const TalkToExportPage = React.lazy(() => import("./pages/talk-to-export"));
const TalkToExportDetailPage = React.lazy(() =>
  import("./pages/talk-to-export/detail")
);
const QuoteRequestPage = React.lazy(() => import("./pages/quote-request"));
const SampleRequestPage = React.lazy(() => import("./pages/sample-request"));
const NewsletterSubscribersPage = React.lazy(() =>
  import("./pages/newsletter-subscribers")
);
const PrivacyPolicyPage = React.lazy(() => import("./pages/Privacy-policy"));
const FaqPage = React.lazy(() => import("./pages/Faq"));
const BlogPage = React.lazy(() => import("./pages/blog"));
const BlogAuthorsPage = React.lazy(() => import("./pages/blog-authors"));
const CareerPage = React.lazy(() => import("./pages/career"));
const CareerApplicationsPage = React.lazy(() =>
  import("./pages/career-applications")
);
const Page401 = React.lazy(() => import("./pages/401"));
const Page404 = React.lazy(() => import("./pages/404"));
const TermsConditionsPage = React.lazy(() => import("./pages/Termsconditions"));
const CookiesPage = React.lazy(() => import("./pages/cookies"));
const BlogDetailPage = React.lazy(() => import("./pages/blog/blogDetail"));
const ChangePasswordPage = React.lazy(() => import("./pages/change-password"));
const MediaExhibitionHighlightsPage = React.lazy(() =>
  import("./pages/media-exhibition-highlights")
);
const ExhibitionBannerImagesPage = React.lazy(() =>
  import("./pages/exhibition-banner-images")
);
const BannerImagesPage = React.lazy(() => import("./pages/banner-images"));
const ProductCategoryPage = React.lazy(() =>
  import("./pages/product-category")
);
const ProductsPage = React.lazy(() => import("./pages/products"));
const ProductDetailPage = React.lazy(() => import("./pages/products/detail"));
const ApplicationsPage = React.lazy(() => import("./pages/applications"));
const OurTeamPage = React.lazy(() => import("./pages/our-team"));
const IndustriesPage = React.lazy(() => import("./pages/industries"));
const IndustriesDetailPage = React.lazy(() =>
  import("./pages/industries/detail")
);
const SeoPage = React.lazy(() => import("./pages/seo"));
const OurPopularProductPage = React.lazy(() =>
  import("./pages/our-popular-product")
);
const MarketplaceLocationsPage = React.lazy(() =>
  import("./pages/marketplace-locations/index.js")
);
const MarketplaceLocationFormPage = React.lazy(() =>
  import("./pages/marketplace-locations/LocationFormPage.js")
);
const MarketplaceLocationProductsPage = React.lazy(() =>
  import("./pages/marketplace-locations/LocationProductsPage.js")
);
const MarketplaceDefaultProductsPage = React.lazy(() =>
  import("./pages/marketplace-default-products/index.js")
);

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>;
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>;
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>;
  }
};

function App() {
  const aclAbilities = defaultACLObj;

  return (
    <Suspense fallback={<FallbackSpinner />}>
      <AclGuard aclAbilities={aclAbilities}>
        <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path="/401" element={<Page401 />} />
            <Route path="/404" element={<Page404 />} />

            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path="/login" element={<LoginPage />}></Route>
                <Route
                  path="/forgot-password"
                  element={<ForgotPassword />}
                ></Route>
              </Route>
            </Route>
          </Route>

          <Route
            element={
              <UserLayout>
                <Outlet />
              </UserLayout>
            }
          >
            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path="/dashboard" element={<DashBoardPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/talk-to-expert" element={<TalkToExpertPage />} />
              <Route path="/talk-to-export" element={<TalkToExportPage />} />
              <Route
                path="/talk-to-export/:id"
                element={<TalkToExportDetailPage />}
              />
              <Route path="/quote-request" element={<QuoteRequestPage />} />
              <Route path="/sample-request" element={<SampleRequestPage />} />
              <Route
                path="/newsletter-subscribers"
                element={<NewsletterSubscribersPage />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route
                path="/terms-conditions"
                element={<TermsConditionsPage />}
              />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog-authors" element={<BlogAuthorsPage />} />
              <Route path="/blog/:id" element={<BlogDetailPage />} />
              <Route path="/career" element={<CareerPage />} />
              <Route
                path="/career-applications"
                element={<CareerApplicationsPage />}
              />
              <Route
                path="/media-exhibition-highlights"
                element={<MediaExhibitionHighlightsPage />}
              />
              <Route
                path="/exhibition-banner-images"
                element={<ExhibitionBannerImagesPage />}
              />
              <Route path="/banner-images" element={<BannerImagesPage />} />
              <Route
                path="/product-category"
                element={<ProductCategoryPage />}
              />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/our-team" element={<OurTeamPage />} />
              <Route
                path="/our-popular-product"
                element={<OurPopularProductPage />}
              />
              <Route path="/industries" element={<IndustriesPage />} />
              <Route path="/seo" element={<SeoPage />} />
              <Route
                path="/industries/:id"
                element={<IndustriesDetailPage />}
              />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route
                path="/marketplace-locations"
                element={<MarketplaceLocationsPage />}
              />
              <Route
                path="/marketplace-locations/new"
                element={<MarketplaceLocationFormPage />}
              />
              <Route
                path="/marketplace-locations/:id/products"
                element={<MarketplaceLocationProductsPage />}
              />
              <Route
                path="/marketplace-locations/:id"
                element={<MarketplaceLocationFormPage />}
              />
              <Route
                path="/marketplace-default-products"
                element={<MarketplaceDefaultProductsPage />}
              />
              <Route
                path="/"
                element={<Navigate to="/dashboard" replace />}
              ></Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </AclGuard>
    </Suspense>
  );
}

export default App;
